import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ChevronRight } from "@mui/icons-material";
import {
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import useUnpaidInvoices from "../hooks/useUnpaidInvoices";
import ProfileDialog from "./ProfileDialog";

const CampaignFlyoutMenuItem = ({ isExpanded, isMobile, location, navigate, onFlyoutOpenChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRef = useRef(null);
  const closeTimerRef = useRef(null);
  const open = Boolean(anchorEl);
  const isRunning = location.pathname.includes("/campaign/running");
  const isSponsored = location.pathname.startsWith("/campaign") && !isRunning;

  useEffect(() => {
    onFlyoutOpenChange?.(open);
  }, [open, onFlyoutOpenChange]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    if (isMobile || !isExpanded) return;
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setAnchorEl(null), 500);
  };

  const handleCampaignRowMouseLeave = (e) => {
    if (isMobile || !isExpanded) return;
    const t = e.relatedTarget;
    if (t && t instanceof Element && t.closest(".MuiPopover-root")) return;
    scheduleClose();
  };

  const openMenu = () => {
    if (isMobile || !isExpanded) return;
    clearCloseTimer();
    if (buttonRef.current) setAnchorEl(buttonRef.current);
  };

  useEffect(() => {
    setAnchorEl(null);
  }, [location.pathname]);

  useEffect(() => () => clearCloseTimer(), []);

  return (
    <>
      <div
        className={`menu-item ${location.pathname.startsWith("/campaign") ? "active" : ""}`}
        onMouseEnter={openMenu}
        onMouseLeave={handleCampaignRowMouseLeave}
      >
        <button
          ref={buttonRef}
          type="button"
          className="menu-link"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={(e) => {
            e.preventDefault();
            clearCloseTimer();
            setAnchorEl((a) => (a ? null : buttonRef.current));
          }}
        >
          <span className="menu-icon material-icon">campaign</span>
          <span
            className="menu-text"
            style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", justifyContent: "flex-start" }}
          >
            Campaign
            {isExpanded && (
              <ChevronRight sx={{ fontSize: "1.1rem", opacity: 0.5, ml: "auto", flexShrink: 0 }} aria-hidden />
            )}
          </span>
        </button>
      </div>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableRestoreFocus
        disableScrollLock
        marginThreshold={0}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        PaperProps={{
          onMouseEnter: () => {
            if (!isMobile && isExpanded) clearCloseTimer();
          },
          onMouseLeave: () => {
            if (!isMobile && isExpanded) scheduleClose();
          },
          sx: {
            ml: 0.5,
            borderRadius: "14px",
            minWidth: 248,
            boxShadow: "0 12px 40px rgba(15, 23, 42, 0.14)",
            border: "1px solid rgba(226, 232, 240, 0.95)",
            overflow: "hidden",
            pointerEvents: "auto",
          },
        }}
      >
        <ListItemButton
          selected={isSponsored}
          onClick={() => {
            navigate("/campaign");
            setAnchorEl(null);
          }}
          sx={{
            py: 1.35,
            px: 2,
            "&.Mui-selected": { bgcolor: "rgba(37, 99, 235, 0.09)" },
          }}
        >
          <ListItemText
            primary="Sponsored listing"
            primaryTypographyProps={{ fontWeight: isSponsored ? 700 : 500, fontSize: "0.9375rem", color: "#0f172a" }}
          />
        </ListItemButton>
        <ListItemButton
          selected={isRunning}
          onClick={() => {
            navigate("/campaign/running");
            setAnchorEl(null);
          }}
          sx={{
            py: 1.35,
            px: 2,
            "&.Mui-selected": { bgcolor: "rgba(37, 99, 235, 0.09)" },
          }}
        >
          <ListItemText
            primary="Running campaigns"
            primaryTypographyProps={{ fontWeight: isRunning ? 700 : 500, fontSize: "0.9375rem", color: "#0f172a" }}
          />
        </ListItemButton>
      </Popover>
    </>
  );
};

const menuItems = [
  { label: "Dashboard", icon: "dashboard", path: "/dashboard" },
  { label: "Reservations", icon: "calendar_today", path: "/reservations" },
  { label: "Rates Calendar", icon: "inventory", path: "rates-inventory" },
  { label: "Promotions", icon: "local_offer", path: "/promotions" },
  { label: "Property Boost", icon: "rocket_launch", path: "/property-boost" },
  { label: "Campaign", icon: "campaign", path: "__campaign_flyout__" },
  { label: "Performance", icon: "trending_up", path: "/performance" },
  { label: "Room Types", icon: "hotel", path: "/room-types" },
  { label: "Settings", icon: "settings", path: "/settings" },
  { label: "Invoice", icon: "receipt", path: "/invoice" },
  // Property Images option will be conditionally rendered below
];

const Sidebar = ({ isOpen, isMobile, isAuthenticated, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [hasHotelId, setHasHotelId] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  /** While Campaign submenu is open (portaled), do not auto-collapse the rail — cursor crosses x > rail before hitting the popover. */
  const [campaignFlyoutOpen, setCampaignFlyoutOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const sidebarMenuRef = useRef(null);
  const { hasUnpaidInvoices } = useUnpaidInvoices();
  
  // // Debug logging for red dot visibility
  // useEffect(() => {
  //   console.log('🔴 Sidebar - hasUnpaidInvoices:', hasUnpaidInvoices);
  // }, [hasUnpaidInvoices]);
  
  useEffect(() => {
    const checkHotelId = () => {
      const hotelId = localStorage.getItem("selectedHotelId");
      setHasHotelId(!!hotelId);
    };
    
    checkHotelId();
    window.addEventListener('storage', checkHotelId);
    
    // Add polling as a fallback for same-tab updates
    const interval = setInterval(checkHotelId, 500);
    
    return () => {
      window.removeEventListener('storage', checkHotelId);
      clearInterval(interval);
    };
  }, []);

  // Add wheel event handling for better scrolling
  useEffect(() => {
    const sidebarMenu = sidebarMenuRef.current;
    if (!sidebarMenu) return;

    const handleWheel = (e) => {
      // Only handle wheel events when sidebar is expanded or on mobile
      if (!isExpanded && !isMobile) return;
      
      e.preventDefault();
      sidebarMenu.scrollTop += e.deltaY;
    };

    sidebarMenu.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      sidebarMenu.removeEventListener('wheel', handleWheel);
    };
  }, [isExpanded, isMobile]);

  /**
   * Desktop: expanded rail (260px) can sit under main content in the 80–260px band; native mouseleave on <aside>
   * is unreliable. Collapse when the pointer moves clearly to the right, unless it's over the Campaign Popover (portal).
   */
  useEffect(() => {
    if (isMobile || !isExpanded) return;
    const COLLAPSE_AFTER_X = 272;
    let raf = 0;
    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (campaignFlyoutOpen) return;
        if (e.clientX <= COLLAPSE_AFTER_X) return;
        const el = document.elementFromPoint(e.clientX, e.clientY);
        if (el?.closest?.(".MuiPopover-root")) return;
        setIsExpanded(false);
      });
    };
    document.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("mousemove", onMove);
    };
  }, [isMobile, isExpanded, campaignFlyoutOpen]);

  const isItemActive = (itemPath) => {
    if (itemPath === "rates-inventory" && location.pathname.includes("room-rates-calendar")) {
      return true;
    }
    if (itemPath === "/property-images" && location.pathname === "/hotel-images") {
      return true;
    }
    if (itemPath === "__campaign_flyout__") {
      return location.pathname.startsWith("/campaign");
    }
    return location.pathname === itemPath;
  };
  
  const handleLogout = () => {
    if (onLogout) {
      onLogout();
      navigate("/login");
    }
  };

  const handleRatesInventoryClick = (e) => {
    e.preventDefault();
      const hotelId = localStorage.getItem("selectedHotelId");
    if (hotelId) {
      window.dispatchEvent(new Event('sidebarRoomRatesCalendarClicked'));
      navigate(`/room-rates-calendar/${hotelId}`);
    } else {
      // toast.error("Please select a hotel from the dashboard first"); // Disabled
      navigate("/");
    }
  };

  // const handleAdminAccess = () => {
  //   const password = prompt("Please enter admin password:");
  //   if (password === "admintab") {
  //     navigate('/partner_registrationform?key=tabtrips2024');
  //     toast.success("Admin access granted!");
  //   } else if (password !== null) {
  //     toast.error("Incorrect password!");
  //   }
  // };

  return (
    <aside 
      className={`sidebar ${isOpen ? 'open' : ''} ${isMobile ? 'mobile' : ''} ${isExpanded ? 'expanded' : ''}`}
      onMouseEnter={() => !isMobile && setIsExpanded(true)}
    >
      <div className="sidebar-content">
        <nav className="sidebar-menu" ref={sidebarMenuRef}>
          {menuItems.map((item, index) => {
            if (item.path === "rates-inventory") {
              return (
                <div 
                  key={index}
                  className={`menu-item ${isItemActive(item.path) ? 'active' : ''}`}
                >
                  <a href="#" className="menu-link" onClick={hasHotelId ? handleRatesInventoryClick : undefined} style={{ pointerEvents: hasHotelId ? 'auto' : 'none', opacity: hasHotelId ? 1 : 0.5 }}>
                    <span className="menu-icon material-icon">{item.icon}</span>
                    <span className="menu-text">{item.label}</span>
                  </a>
                </div>
              );
            }
            if (item.path === "__campaign_flyout__") {
              return (
                <CampaignFlyoutMenuItem
                  key={index}
                  isExpanded={isExpanded}
                  isMobile={isMobile}
                  location={location}
                  navigate={navigate}
                  onFlyoutOpenChange={setCampaignFlyoutOpen}
                />
              );
            }
            return (
              <div 
                key={index}
                className={`menu-item ${isItemActive(item.path) ? 'active' : ''}`}
              >
                <Link to={item.path} className="menu-link">
                  <span className="menu-icon material-icon">
                    {item.icon}
                    {item.path === '/invoice' && hasUnpaidInvoices && (
                      <span className="unpaid-indicator"></span>
                    )}
                  </span>
                  <span className="menu-text">{item.label}</span>
                </Link>
              </div>
            );
          })}
          {/* Always render Property Images option - not conditional */}
            <div className={`menu-item ${isItemActive('/property-images') ? 'active' : ''}`}>
              <Link to="/property-images" className="menu-link">
                <span className="menu-icon material-icon">photo_library</span>
                <span className="menu-text">Property Images</span>
              </Link>
            </div>
        </nav>
        
        <div className="sidebar-footer">
          {/* <button 
            onClick={handleAdminAccess}
            className="admin-access-btn"
          >
            <span className="menu-icon material-icon">admin_panel_settings</span>
            <span className="menu-text">Admin Access</span>
          </button> */}

          {isAuthenticated && isTabletOrMobile && (
            <button className="profile-button" onClick={() => setProfileDialogOpen(true)}>
              <span className="menu-icon material-icon">person</span>
              <span className="menu-text">Profile</span>
            </button>
          )}

          {isAuthenticated && (
            <button className="logout-button" onClick={handleLogout}>
              <span className="menu-icon material-icon">logout</span>
              <span className="menu-text">Logout</span>
            </button>
          )}
        </div>
      </div> 
      
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap');
        @import url('https://fonts.googleapis.com/icon?family=Material+Icons');
        
        .sidebar {
          position: fixed;
          top: 60px;
          left: 0;
          bottom: 0;
          width: 80px;
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(10px);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
          z-index: 1100;
          font-family: 'Poppins', sans-serif;
          border-right: 1px solid rgba(0, 0, 0, 0.05);
          margin: 0;
          padding: 0;
        }

        .sidebar.expanded {
          width: 260px;
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 4px 0 24px rgba(0, 0, 0, 0.08);
        }

        .sidebar.mobile {
          transform: translateX(-100%);
          background: rgba(255, 255, 255, 0.98);
          box-shadow: none;
          width: 260px;
        }

        .sidebar.mobile.open {
          transform: translateX(0);
          box-shadow: 4px 0 24px rgba(0, 0, 0, 0.12);
        }

        .sidebar-content {
          height: 100%;
          display: flex;
          flex-direction: column;
          padding: 0.75rem 0.25rem;
          min-height: 0;
          margin: 0;
        }

        .sidebar.expanded .sidebar-content {
          padding: 1rem 1.25rem;
        }

        .sidebar-menu {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          overflow-y: auto;
          overflow-x: hidden;
          scrollbar-width: thin;
          scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
          max-height: calc(100vh - 140px);
          scroll-behavior: smooth;
          position: relative;
          margin: 0;
          padding: 0;
        }

        .sidebar-menu::after {
          content: '';
          position: sticky;
          bottom: 0;
          left: 0;
          right: 0;
          height: 20px;
          background: linear-gradient(to top, rgba(255, 255, 255, 0.8) 0%, transparent 100%);
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .sidebar.expanded .sidebar-menu::after {
          opacity: 1;
        }

        .sidebar-menu::-webkit-scrollbar {
          width: 4px;
          transition: width 0.3s ease;
        }

        .sidebar.expanded .sidebar-menu::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-menu::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 3px;
        }

        .sidebar.expanded .sidebar-menu::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
        }

        .sidebar-menu::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 3px;
          transition: background 0.3s ease;
        }

        .sidebar.expanded .sidebar-menu::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.3);
        }

        .sidebar-menu::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.4);
        }

        .menu-item {
          position: relative;
          border-radius: 14px;
          overflow: hidden;
          transition: transform 0.2s ease;
          width: 100%;
          display: flex;
          align-items: center;
        }

        .menu-item:hover {
          transform: translateX(4px);
        }

        .menu-link {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          padding: 1rem 0.5rem;
          color: #2E4057;
          text-decoration: none;
          transition: all 0.3s ease;
          position: relative;
          z-index: 1;
          white-space: nowrap;
          min-height: 3.5rem;
          margin: 0;
        }

        button.menu-link {
          border: none;
          background: transparent;
          font: inherit;
          width: 100%;
          cursor: pointer;
          text-align: left;
        }

        .sidebar.expanded .menu-link {
          padding: 1rem 1.25rem;
          justify-content: flex-start;
        }

        .menu-item.active .menu-link {
          color: #FF7700;
          font-weight: 500;
        }

        .menu-item.active {
          background: rgba(255, 119, 0, 0.08);
        }

        .menu-item.active::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: #FF7700;
          border-radius: 0 4px 4px 0;
        }

        .menu-icon {
          font-family: 'Material Icons';
          font-size: 1.5rem;
          width: 2.5rem;
          height: 2.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: inherit;
          transition: all 0.3s ease;
          border-radius: 10px;
          margin: 0;
          flex-shrink: 0;
          position: relative;
        }

        .sidebar.expanded .menu-icon {
          margin-right: 1rem;
        }

        .menu-item:hover .menu-icon {
          color: #FF7700;
          transform: scale(1.1);
        }

        .menu-item.active .menu-icon {
          color: #FF7700;
          background: rgba(255, 119, 0, 0.1);
        }

        .menu-text {
          font-size: 0.9375rem;
          font-weight: 500;
          letter-spacing: 0.3px;
          color: inherit;
          opacity: 0;
          transform: translateX(-10px);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          line-height: 1.2;
        }

        .sidebar.expanded .menu-text {
          opacity: 1;
          transform: translateX(0);
        }

        .sidebar-footer {
          margin-top: auto;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          padding-top: 0.5rem;
          border-top: 1px solid rgba(0, 0, 0, 0.06);
        }

        .admin-access-btn,
        .profile-button,
        .logout-button {
          width: 100%;
          display: flex;
          align-items: center;
          padding: 0.6rem;
          background: none;
          border: none;
          cursor: pointer;
          color: #2E4057;
          border-radius: 10px;
          transition: all 0.3s ease;
        }

        .sidebar.expanded .admin-access-btn,
        .sidebar.expanded .profile-button,
        .sidebar.expanded .logout-button {
          padding: 0.7rem 1rem;
        }

        .admin-access-btn {
          opacity: 0.1;
        }

        .admin-access-btn:hover {
          opacity: 1;
          background: rgba(0, 0, 0, 0.03);
        }

        .profile-button {
          background: rgba(255, 119, 0, 0.05);
          border: 1px solid rgba(255, 119, 0, 0.15);
          margin-bottom: 0.5rem;
        }

        .profile-button:hover {
          background: rgba(255, 119, 0, 0.1);
          color: #FF7700;
          border-color: rgba(255, 119, 0, 0.25);
        }

        .logout-button {
          background: rgba(255, 119, 0, 0.03);
          border: 1px solid rgba(255, 119, 0, 0.1);
        }

        .logout-button:hover {
          background: rgba(255, 119, 0, 0.08);
          color: #FF7700;
          border-color: rgba(255, 119, 0, 0.2);
        }

        @media (max-width: 768px) {
          .sidebar {
            width: 260px;
          }
          
          .sidebar-content {
            padding: 0.5rem 0.5rem;
            min-height: 0;
          }
          
          .sidebar-menu {
            gap: 0.4rem;
            overflow-y: auto;
            overflow-x: hidden;
            max-height: calc(100vh - 100px);
            scroll-behavior: smooth;
          }
          
          .menu-link {
            padding: 0.6rem 0.75rem;
          }
          
          .menu-text {
            opacity: 1;
            transform: translateX(0);
            font-size: 0.8rem;
            font-weight: 500;
          }
          
          .menu-icon {
            margin: 0;
            margin-right: 0.6rem;
            font-size: 1.3rem;
            width: 1.8rem;
            height: 1.8rem;
          }
          
          .sidebar-footer {
            gap: 0.2rem;
            padding-top: 0.3rem;
          }
          
          .admin-access-btn,
          .profile-button,
          .logout-button {
            padding: 0.5rem 0.75rem;
            font-size: 0.8rem;
          }
          
          .profile-button {
            margin-bottom: 0.4rem;
          }
          
          .menu-item {
            border-radius: 10px;
          }
          
          .menu-item:hover {
            transform: translateX(2px);
          }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }

        .menu-item {
          animation: fadeIn 0.3s ease forwards;
        }

        .unpaid-indicator {
          position: absolute;
          top: 2px;
          right: -2px;
          width: 12px;
          height: 12px;
          background: #f44336;
          border-radius: 50%;
          border: 2px solid white;
          box-shadow: 
            0 0 0 0 rgba(244, 67, 54, 0.7),
            0 0 0 0 rgba(244, 67, 54, 0.7),
            0 2px 8px rgba(244, 67, 54, 0.4);
          animation: glow 1.5s infinite;
          z-index: 10;
        }

        .sidebar.expanded .unpaid-indicator {
          top: 3px;
          right: -3px;
          width: 14px;
          height: 14px;
        }

        @keyframes glow {
          0% {
            transform: scale(1);
            box-shadow: 
              0 0 0 0 rgba(244, 67, 54, 0.7),
              0 0 0 0 rgba(244, 67, 54, 0.7),
              0 2px 8px rgba(244, 67, 54, 0.4);
          }
          50% {
            transform: scale(1.1);
            box-shadow: 
              0 0 0 4px rgba(244, 67, 54, 0.3),
              0 0 0 8px rgba(244, 67, 54, 0.1),
              0 2px 12px rgba(244, 67, 54, 0.6);
          }
          100% {
            transform: scale(1);
            box-shadow: 
              0 0 0 0 rgba(244, 67, 54, 0.7),
              0 0 0 0 rgba(244, 67, 54, 0.7),
              0 2px 8px rgba(244, 67, 54, 0.4);
          }
        }
      `}</style>

      {/* Profile Dialog */}
      <ProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        onLogout={handleLogout}
      />
    </aside>
  );
};

export default Sidebar;
